import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import * as Routes from '@/modules/routes';
import { hasScope } from './useSyncRows';
import type { SyncFilters } from './useSyncRows';

export type AssignmentStatus =
  | 'delivering' | 'partial' | 'not_delivering' | 'nothing_to_sync' | 'covered_by_twin' | 'rejected'
  | 'not_in_lms';

export interface AssignmentRow {
  assignment_id: number;
  assignment_title: string;
  /** "Unit 1 - Lesson 3 - Title", the small line over the label on the teacher's Assignments table. */
  location: string | null;
  /** "Reading + Checks + Notes (2 items)", the teacher's own name for it. */
  label: string | null;
  items: string[];
  class_names: string[];
  due_at: string | null;
  teacher: string | null;
  teacher_id: number | null;
  classes: number;
  courses: number;
  linked: boolean;
  students_with_scores: number;
  delivered: number;
  /** Of its undelivered scored students, how many a duplicate on the same content delivered. */
  twin_covered: number;
  /** Scored students whose grade is in the LMS from no assignment at all. */
  uncovered: number;
  stale: number;
  last_delivery: string | null;
  /** Students still assigned whose latest post the LMS refused. */
  rejected_now: number;
  /** For a never-linked copy covered by linked ones: those copies, where its missing scores are owed. */
  served_by?: number[];
  status: AssignmentStatus;
  diagnosis: string[];
  /** Finding codes, for grouping rows by the repair they need. */
  codes: string[];
  owner?: 'tci' | 'lms' | 'join';
  attempts?: number;
  accepted?: number;
  failed?: number;
  refused_locally?: number;
  /** Posts the worker skipped with a reason (not launched, student never launched…). */
  skipped?: number;
  last_attempt?: string | null;
}

interface AssignmentRowsPayload {
  rows: AssignmentRow[];
  scoped: boolean;
}

const useAssignmentRows = (filters: SyncFilters, enabled: boolean) => useQuery({
  enabled: enabled && hasScope(filters),
  queryFn: async (): Promise<AssignmentRowsPayload> => {
    const { data } = await axios.get(Routes.plato_api_sysadmin_grade_sync_assignments_path(), {
      params: filters,
    });

    return data.data;
  },
  queryKey: ['gradeSyncAssignments', filters],
});

export default useAssignmentRows;
